import { createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Flag, Send } from "lucide-solid";

import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useI18n } from "@/providers/i18n-provider";
import { REPORT_REASONS, reportPostMutation, type ReportReason } from "@famidex/shared";

/**
 * 🚩 Reportar publicación — motivos + detalle opcional → `reportPostMutation`
 * (idempotente por usuario). La cola de moderación es de Wave 1-G. Traducción del
 * diseño Stitch `reportar_publicacion` sobre el Dialog de Kobalte (CC-17: role
 * "dialog").
 */
export function ReportPostDialog(props: {
  postId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReported?: () => void;
}) {
  const session = useSession();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  const [reason, setReason] = createSignal<ReportReason | null>(null);
  const [details, setDetails] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [done, setDone] = createSignal(false);

  const reasonLabel = (r: ReportReason): string => {
    switch (r) {
      case "inappropriate": {
        return t(msg`Contenido inapropiado`);
      }
      case "spam": {
        return t(msg`Spam o engañoso`);
      }
      case "copyright": {
        return t(msg`Infracción de derechos`);
      }
      default: {
        return t(msg`Otro motivo`);
      }
    }
  };

  const submit = async () => {
    const r = reason();
    const db = drizzle();
    const uid = session.userId();
    if (!r || !db || !uid || !session.isAuthenticated()) {
      return;
    }
    setBusy(true);
    try {
      await reportPostMutation(db, uid, props.postId, r, details().trim() || null);
      setDone(true);
      props.onReported?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-md">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2 text-lg font-semibold text-foreground">
          <Flag class="h-5 w-5 text-destructive" />
          {t(msg`Reportar contenido`)}
        </DialogTitle>
      </DialogHeader>

      <div class="flex flex-col gap-5 p-4">
        <Show
          when={!done()}
          fallback={
            <div class="grid place-items-center gap-2 py-6 text-center">
              <Flag class="h-8 w-8 text-primary" />
              <p class="text-sm text-foreground">{t(msg`Gracias. Nuestro equipo lo revisará.`)}</p>
            </div>
          }
        >
          <p class="text-sm text-muted-foreground">
            {t(msg`Ayúdanos a cuidar la comunidad. Elige el motivo principal.`)}
          </p>

          <fieldset class="flex flex-col gap-2" data-testid="report-reasons">
            <For each={REPORT_REASONS}>
              {(r) => (
                <label
                  class="flex cursor-pointer items-center gap-3 rounded-lg border border-border/60 p-3 text-sm transition-colors hover:border-primary"
                  classList={{ "border-primary bg-accent/40": reason() === r }}
                >
                  <input
                    type="radio"
                    name="report_reason"
                    value={r}
                    checked={reason() === r}
                    onChange={() => setReason(r)}
                    class="h-4 w-4 accent-primary"
                  />
                  <span class="font-medium text-foreground">{reasonLabel(r)}</span>
                </label>
              )}
            </For>
          </fieldset>

          <textarea
            data-testid="report-details"
            value={details()}
            onInput={(e) => setDetails(e.currentTarget.value)}
            rows={3}
            placeholder={t(msg`Detalles adicionales (opcional)`)}
            class="w-full resize-none rounded-lg border border-border/60 bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-primary"
          />

          <div class="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                props.onOpenChange(false);
              }}
              class="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              {t(msg`Cancelar`)}
            </button>
            <button
              type="button"
              data-testid="submit-report"
              disabled={!reason() || busy()}
              onClick={() => void submit()}
              class="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {t(msg`Enviar reporte`)}
              <Send class="h-4 w-4" />
            </button>
          </div>
        </Show>
      </div>
    </Dialog>
  );
}
